import { useEffect, useState } from "react";
import { crewOpsApi, dgcaApi } from "@/api";
import {
  CheckSquare, AlertTriangle, RefreshCw, Shield, CheckCircle,
  Download, Moon, TrendingUp, AlertCircle, Clock, Globe, Users,
} from "lucide-react";
import { exportPDF, exportCSV, todayStr } from "@/utils/exportUtils";

const STATUS_CFG = {
  breach:  { label: "BREACH",  cls: "text-red-400 bg-red-500/10 border-red-500/30",   dot: "bg-red-500" },
  watch:   { label: "WATCH",   cls: "text-amber-400 bg-amber-500/10 border-amber-500/30", dot: "bg-amber-500" },
  caution: { label: "CAUTION", cls: "text-yellow-400 bg-yellow-500/10 border-yellow-500/30", dot: "bg-yellow-500" },
  ok:      { label: "OK",      cls: "text-green-400 bg-green-500/10 border-green-500/30",  dot: "bg-green-500" },
};

function ProgressBar({ pct, status }) {
  const color = status === "breach" ? "bg-red-500" : status === "watch" ? "bg-amber-500" : status === "caution" ? "bg-yellow-500" : "bg-green-500";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className={`text-[10px] font-mono font-bold w-8 text-right ${STATUS_CFG[status]?.cls?.split(" ")[0] || "text-foreground"}`}>{pct}%</span>
    </div>
  );
}

function DGCATab() {
  const [fdpData,   setFdpData]   = useState(null);
  const [woclData,  setWoclData]  = useState(null);
  const [augData,   setAugData]   = useState(null);
  const [acclData,  setAcclData]  = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [subTab,    setSubTab]    = useState("cumulative");

  useEffect(() => {
    Promise.all([
      dgcaApi.getCumulativeFdp(),
      dgcaApi.getWoclDuties(),
      dgcaApi.getAugmentedCrew(),
      dgcaApi.getAcclimatisation(),
    ]).then(([f, w, a, acc]) => {
      setFdpData(f.data);
      setWoclData(w.data);
      setAugData(a.data);
      setAcclData(acc.data);
    }).finally(() => setLoading(false));
  }, []);

  function downloadFdpPDF() {
    if (!fdpData) return;
    exportPDF({
      title: "DGCA Cumulative FDP Compliance Report",
      subtitle: `${fdpData.reference} · Generated: ${new Date().toLocaleDateString("en-IN")}`,
      columns: [
        { header: "Crew Member",   key: "name",          width: 42 },
        { header: "Rank",          key: "rank",          width: 18 },
        { header: "Base",          key: "base",          width: 14 },
        { header: "7-Day FDP (h)", key: "fdp_7day",      width: 20 },
        { header: "Limit 60h",     key: "pct_7day_str",  width: 16 },
        { header: "7-Day Status",  key: "status_7day",   width: 18 },
        { header: "28-Day FDP (h)",key: "fdp_28day",     width: 22 },
        { header: "Limit 190h",    key: "pct_28day_str", width: 16 },
        { header: "28-Day Status", key: "status_28day",  width: 20 },
        { header: "365-Day Block", key: "block_365day",  width: 22 },
        { header: "Limit 1000h",   key: "pct_365day_str",width: 18 },
        { header: "Overall",       key: "overall_status",width: 18 },
      ],
      rows: (fdpData.crew || []).map(c => ({
        ...c,
        pct_7day_str:   `${c.pct_7day}%`,
        pct_28day_str:  `${c.pct_28day}%`,
        pct_365day_str: `${c.pct_365day}%`,
        status_7day:    c.status_7day.toUpperCase(),
        status_28day:   c.status_28day.toUpperCase(),
        overall_status: c.overall_status.toUpperCase(),
      })),
      filename: `dgca-cumulative-fdp-${todayStr()}.pdf`,
    });
  }

  function downloadWoclPDF() {
    if (!woclData) return;
    exportPDF({
      title: "DGCA Night / WOCL Duty Analysis Report",
      subtitle: `WOCL Window: ${woclData.wocl_window} · ${woclData.reference}`,
      columns: [
        { header: "Crew Member",   key: "crew_name",       width: 40 },
        { header: "Rank",          key: "rank",            width: 16 },
        { header: "Pairing",       key: "pairing_code",    width: 22 },
        { header: "Duty Date",     key: "duty_date",       width: 22 },
        { header: "Report Time",   key: "report_time",     width: 18 },
        { header: "Type",          key: "wocl_type_label", width: 16 },
        { header: "Route",         key: "route",           width: 16 },
        { header: "Reduction",     key: "reduction",       width: 16 },
        { header: "Max FDP (h)",   key: "fdp_limit_reduced",width: 18 },
        { header: "Actual FDP (h)",key: "actual_fdp",      width: 20 },
        { header: "Status",        key: "status_label",    width: 16 },
      ],
      rows: (woclData.duties || []).map(d => ({
        ...d,
        wocl_type_label: d.wocl_type === "wocl" ? "WOCL" : "EARLY",
        status_label:    d.status.toUpperCase(),
      })),
      filename: `dgca-wocl-duties-${todayStr()}.pdf`,
    });
  }

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-muted-foreground">
      <RefreshCw size={16} className="animate-spin mr-2" /> Loading DGCA data...
    </div>
  );

  const summary = fdpData?.summary || {};
  const woclSum = woclData?.summary || {};

  return (
    <div className="space-y-5">
      {/* Summary badges */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "Breach",  value: summary.breach,  color: "text-red-400",    border: "border-red-500/20",    bg: "bg-red-500/5" },
          { label: "Watch",   value: summary.watch,   color: "text-amber-400",  border: "border-amber-500/20",  bg: "bg-amber-500/5" },
          { label: "Caution", value: summary.caution, color: "text-yellow-400", border: "border-yellow-500/20", bg: "bg-yellow-500/5" },
          { label: "OK",      value: summary.ok,      color: "text-green-400",  border: "border-green-500/20",  bg: "bg-green-500/5" },
        ].map(({ label, value, color, border, bg }) => (
          <div key={label} className={`${bg} border ${border} rounded-sm p-4`}>
            <div className={`text-3xl font-mono font-medium ${color}`}>{value ?? 0}</div>
            <div className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">{label}</div>
          </div>
        ))}
      </div>

      {/* Sub-tab selector */}
      <div className="flex gap-1 bg-background/60 border border-border rounded-sm p-1 w-fit flex-wrap">
        {[
          { id: "cumulative", label: "Cumulative FDP",    icon: TrendingUp },
          { id: "wocl",       label: "Night / WOCL",      icon: Moon },
          { id: "augmented",  label: "Augmented Crew",    icon: Users },
          { id: "accl",       label: "Acclimatisation",   icon: Globe },
        ].map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setSubTab(id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-bold transition-colors ${subTab === id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
            data-testid={`dgca-subtab-${id}`}>
            <Icon size={11} /> {label}
          </button>
        ))}
      </div>

      {/* ── Cumulative FDP Table ──────────────────────────────────────────── */}
      {subTab === "cumulative" && (
        <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid="dgca-cumulative-table">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-background/40">
            <div>
              <div className="text-sm font-bold text-foreground">Rolling FDP Accumulators</div>
              <div className="text-xs text-muted-foreground">DGCA CAR Sec 7 Series B — 7-day max 60h · 28-day max 190h · 365-day block max 1000h</div>
            </div>
            <button onClick={downloadFdpPDF}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 border border-primary/30 text-primary rounded-sm text-xs font-bold hover:bg-primary/20 transition-colors"
              data-testid="dgca-download-fdp-pdf">
              <Download size={12} /> Export PDF
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-background/60">
                  <th className="text-left px-4 py-2.5 text-muted-foreground font-bold uppercase tracking-wide">Crew</th>
                  <th className="text-left px-3 py-2.5 text-muted-foreground font-bold uppercase tracking-wide w-16">Rank</th>
                  <th className="text-left px-3 py-2.5 text-muted-foreground font-bold uppercase tracking-wide w-12">Base</th>
                  <th className="px-3 py-2.5 text-muted-foreground font-bold uppercase tracking-wide text-center w-36">7-Day / 60h</th>
                  <th className="px-3 py-2.5 text-muted-foreground font-bold uppercase tracking-wide text-center w-36">28-Day / 190h</th>
                  <th className="px-3 py-2.5 text-muted-foreground font-bold uppercase tracking-wide text-center w-36">365-Day / 1000h</th>
                  <th className="px-3 py-2.5 text-muted-foreground font-bold uppercase tracking-wide text-center w-24">Status</th>
                </tr>
              </thead>
              <tbody>
                {(fdpData?.crew || []).map((c, i) => {
                  const sc = STATUS_CFG[c.overall_status] || STATUS_CFG.ok;
                  return (
                    <tr key={c.crew_id} className={`border-b border-border/40 hover:bg-secondary/20 ${i % 2 === 0 ? "" : "bg-background/20"}`}>
                      <td className="px-4 py-2.5 font-medium text-foreground">{c.name}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{c.rank}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{c.base}</td>
                      <td className="px-3 py-2.5">
                        <div className="text-[11px] text-center mb-1 font-mono">{c.fdp_7day}h</div>
                        <ProgressBar pct={c.pct_7day} status={c.status_7day} />
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="text-[11px] text-center mb-1 font-mono">{c.fdp_28day}h</div>
                        <ProgressBar pct={c.pct_28day} status={c.status_28day} />
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="text-[11px] text-center mb-1 font-mono">{c.block_365day}h</div>
                        <ProgressBar pct={c.pct_365day} status={c.status_365day} />
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`text-[10px] font-black px-2 py-0.5 rounded-sm border ${sc.cls}`}>{sc.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── WOCL Duties Table ─────────────────────────────────────────────── */}
      {subTab === "wocl" && (
        <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid="dgca-wocl-table">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-background/40">
            <div>
              <div className="text-sm font-bold text-foreground">Night / WOCL Duty Analysis</div>
              <div className="text-xs text-muted-foreground">
                WOCL window 02:00–05:59 local · Duties crossing WOCL receive reduced FDP limit
                <span className="ml-2 text-amber-400 font-bold">{woclSum.breach} breach · {woclSum.watch} watch</span>
              </div>
            </div>
            <button onClick={downloadWoclPDF}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 border border-primary/30 text-primary rounded-sm text-xs font-bold hover:bg-primary/20 transition-colors"
              data-testid="dgca-download-wocl-pdf">
              <Download size={12} /> Export PDF
            </button>
          </div>

          {/* WOCL legend */}
          <div className="px-4 py-2 border-b border-border bg-background/30 flex gap-4 text-[11px] text-muted-foreground">
            <span><span className="font-bold text-red-400">WOCL</span> — Duty starts 02:00–05:59. FDP reduced by 2h (max 11h)</span>
            <span><span className="font-bold text-amber-400">EARLY</span> — Duty starts before 06:00. FDP reduced by 1h (max 12h)</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-background/60">
                  {["Crew", "Rank", "Base", "Duty Date", "Report Time", "Type", "Route", "Reduction", "Max FDP", "Actual FDP", "Status"].map(h => (
                    <th key={h} className="text-left px-3 py-2.5 text-muted-foreground font-bold uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(woclData?.duties || []).map((d, i) => {
                  const sc = STATUS_CFG[d.status] || STATUS_CFG.ok;
                  const typeColor = d.wocl_type === "wocl" ? "text-red-400 bg-red-500/10 border-red-500/30" : "text-amber-400 bg-amber-500/10 border-amber-500/30";
                  return (
                    <tr key={d.id} className={`border-b border-border/40 hover:bg-secondary/20 ${i % 2 === 0 ? "" : "bg-background/20"}`}>
                      <td className="px-3 py-2.5 font-medium text-foreground whitespace-nowrap">{d.crew_name}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{d.rank}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{d.base}</td>
                      <td className="px-3 py-2.5 text-muted-foreground font-mono">{d.duty_date}</td>
                      <td className="px-3 py-2.5 font-mono font-bold text-foreground">{d.report_time}</td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-sm border ${typeColor}`}>
                          {d.wocl_type === "wocl" ? "WOCL" : "EARLY"}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 font-mono text-blue-400">{d.route}</td>
                      <td className="px-3 py-2.5 text-amber-400 font-bold">{d.reduction}</td>
                      <td className="px-3 py-2.5 font-mono">{d.fdp_limit_reduced}h</td>
                      <td className={`px-3 py-2.5 font-mono font-bold ${d.actual_fdp > d.fdp_limit_reduced ? "text-red-400" : "text-foreground"}`}>
                        {d.actual_fdp}h
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[10px] font-black px-2 py-0.5 rounded-sm border ${sc.cls}`}>{sc.label}</span>
                      </td>
                    </tr>
                  );
                })}
                {(!woclData?.duties?.length) && (
                  <tr><td colSpan={11} className="px-4 py-8 text-center text-muted-foreground">No WOCL/early duties found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="text-[11px] text-muted-foreground/50 border-t border-border pt-3">
        Reference: {fdpData?.reference} · Data as of {new Date(fdpData?.generated_at || Date.now()).toLocaleString("en-IN")}
      </div>

      {/* ── Augmented Crew Tab ──────────────────────────────────────────────── */}
      {subTab === "augmented" && (
        <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid="dgca-augmented-tab">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div>
              <div className="text-sm font-bold text-foreground">Augmented Crew Compliance</div>
              <div className="text-xs text-muted-foreground">Long-haul pairings requiring 3rd / 4th crew member (DGCA CAR Section 7)</div>
            </div>
            <button onClick={() => augData && exportCSV({
              headers: [
                {label:"Route",key:"route"},{label:"Block Hrs",key:"block_hours"},{label:"Crew Required",key:"crew_required"},
                {label:"Crew Assigned",key:"crew_assigned"},{label:"FDP Limit",key:"fdp_limit_h"},{label:"Status",key:"status"},
                {label:"Regulatory Ref",key:"regulatory_ref"},
              ],
              rows: augData.pairings || [],
              filename: `dgca-augmented-crew-${todayStr()}.csv`,
            })}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 border border-primary/30 text-primary rounded-sm text-xs font-bold hover:bg-primary/20 transition-colors"
              data-testid="dgca-augmented-csv">
              <Download size={12} /> Export CSV
            </button>
          </div>
          <div className="flex gap-3 px-4 py-3 bg-background/30 border-b border-border text-xs text-muted-foreground">
            <span>Total: <span className="font-bold text-foreground">{augData?.summary?.total ?? "—"}</span></span>
            <span>Non-compliant: <span className="font-bold text-red-400">{augData?.summary?.non_compliant ?? "—"}</span></span>
            <span>Compliant: <span className="font-bold text-green-400">{augData?.summary?.compliant ?? "—"}</span></span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-border bg-background/60">
                {["Route","Block Hrs","Crew Req.","Crew Asgn.","FDP Limit","Actual FDP","Gap","Status"].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-muted-foreground font-bold uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {(augData?.pairings || []).map((r, i) => {
                  const isNC = r.status === "non_compliant";
                  return (
                    <tr key={`${r.pairing_id}-${r.route}`} className={`border-b border-border/40 hover:bg-secondary/20 ${isNC ? "bg-red-500/4" : i % 2 ? "bg-background/20" : ""}`}>
                      <td className="px-3 py-2.5 font-mono text-blue-400">{r.route}</td>
                      <td className="px-3 py-2.5 font-mono">{r.block_hours}h</td>
                      <td className={`px-3 py-2.5 font-bold ${r.crew_required >= 4 ? "text-red-400" : r.crew_required >= 3 ? "text-amber-400" : "text-foreground"}`}>{r.crew_required}</td>
                      <td className={`px-3 py-2.5 font-bold ${r.crew_assigned < r.crew_required ? "text-red-400" : "text-green-400"}`}>{r.crew_assigned}</td>
                      <td className="px-3 py-2.5 font-mono text-muted-foreground">{r.fdp_limit_h}h</td>
                      <td className="px-3 py-2.5 font-mono">{r.actual_fdp_h}h</td>
                      <td className={`px-3 py-2.5 font-bold ${r.gap > 0 ? "text-red-400" : "text-green-400"}`}>{r.gap > 0 ? `-${r.gap} crew` : "OK"}</td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[10px] font-black px-1.5 py-0.5 border rounded-sm ${isNC ? "text-red-400 bg-red-500/10 border-red-500/30" : "text-green-400 bg-green-500/10 border-green-500/30"}`}>
                          {isNC ? "NON-COMPLIANT" : "COMPLIANT"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {(!augData?.pairings?.length) && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No long-haul pairings with augmented crew requirements found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Acclimatisation Tab ─────────────────────────────────────────────── */}
      {subTab === "accl" && (
        <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid="dgca-accl-tab">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div>
              <div className="text-sm font-bold text-foreground">Timezone Crossing & Acclimatisation</div>
              <div className="text-xs text-muted-foreground">Routes where crew cross {">"}4 timezone hours — acclimatisation window required per DGCA CAR Section 7</div>
            </div>
            <button onClick={() => acclData && exportCSV({
              headers: [
                {label:"Route",key:"route"},{label:"Dep TZ",key:"dep_tz"},{label:"Arr TZ",key:"arr_tz"},
                {label:"TZ Crossing (h)",key:"tz_crossing"},{label:"Accl Required (h)",key:"accl_required_h"},
                {label:"Status",key:"status"},{label:"Regulatory Ref",key:"regulatory_ref"},
              ],
              rows: acclData.records || [],
              filename: `dgca-acclimatisation-${todayStr()}.csv`,
            })}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 border border-primary/30 text-primary rounded-sm text-xs font-bold hover:bg-primary/20 transition-colors"
              data-testid="dgca-accl-csv">
              <Download size={12} /> Export CSV
            </button>
          </div>
          <div className="flex gap-3 px-4 py-3 bg-background/30 border-b border-border text-xs text-muted-foreground">
            <span>Routes tracked: <span className="font-bold text-foreground">{acclData?.summary?.total ?? "—"}</span></span>
            <span>Accl. Required: <span className="font-bold text-amber-400">{acclData?.summary?.required ?? "—"}</span></span>
            <span>Advisory: <span className="font-bold text-blue-400">{acclData?.summary?.advisory ?? "—"}</span></span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-border bg-background/60">
                {["Route","Dep TZ","Arr TZ","TZ Crossing","Accl. Window","Status","Reg. Ref"].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-muted-foreground font-bold uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {(acclData?.records || []).map((r, i) => {
                  const isReq = r.status === "required";
                  return (
                    <tr key={r.route} className={`border-b border-border/40 hover:bg-secondary/20 ${i % 2 ? "bg-background/20" : ""}`}>
                      <td className="px-3 py-2.5 font-mono text-blue-400">{r.route}</td>
                      <td className="px-3 py-2.5 text-muted-foreground font-mono">{r.dep_tz}</td>
                      <td className="px-3 py-2.5 text-muted-foreground font-mono">{r.arr_tz}</td>
                      <td className={`px-3 py-2.5 font-bold font-mono ${r.tz_crossing >= 6 ? "text-red-400" : r.tz_crossing >= 4 ? "text-amber-400" : "text-foreground"}`}>
                        {r.tz_crossing}h
                      </td>
                      <td className="px-3 py-2.5 font-mono text-amber-400 font-bold">{r.accl_required_h}h</td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[10px] font-black px-1.5 py-0.5 border rounded-sm ${isReq ? "text-amber-400 bg-amber-500/10 border-amber-500/30" : "text-blue-400 bg-blue-500/10 border-blue-500/30"}`}>
                          {isReq ? "REQUIRED" : "ADVISORY"}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground text-[11px] font-mono">{r.regulatory_ref}</td>
                    </tr>
                  );
                })}
                {(!acclData?.records?.length) && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No significant timezone crossings found in current pairings</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ComplianceBoard() {
  const [violations,      setViolations]     = useState([]);
  const [rules,           setRules]          = useState([]);
  const [loading,         setLoading]        = useState(true);
  const [checking,        setChecking]       = useState(false);
  const [filterSeverity,  setFilterSeverity] = useState("");
  const [rosterId,        setRosterId]       = useState(null);
  const [activeTab,       setActiveTab]      = useState("violations");

  const fetchData = async () => {
    const [vRes, rRes, rosRes] = await Promise.all([
      crewOpsApi.getViolations(), crewOpsApi.getRules(), crewOpsApi.getRosters(),
    ]);
    setViolations(vRes.data);
    setRules(rRes.data);
    if (rosRes.data?.length) setRosterId(rosRes.data[0].id);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const runCheck = async () => {
    if (!rosterId) return;
    setChecking(true);
    await crewOpsApi.runComplianceCheck(rosterId);
    await fetchData();
    setChecking(false);
  };

  const filtered      = violations.filter(v => !filterSeverity || v.severity === filterSeverity);
  const errorCount    = violations.filter(v => v.severity === "error").length;
  const warningCount  = violations.filter(v => v.severity === "warning").length;

  function downloadViolationsPDF() {
    exportPDF({
      title: "Compliance Violations Report",
      subtitle: `${new Date().toLocaleString("en-IN", { month: "long", year: "numeric" })} Roster · ${filtered.length} violations`,
      columns: [
        { header: "Severity",    key: "sev_label", width: 18 },
        { header: "Rule",        key: "rule_code", width: 30 },
        { header: "Crew",        key: "crew_name", width: 40 },
        { header: "Description", key: "description", width: 80 },
        { header: "Date",        key: "date",       width: 24 },
        { header: "Status",      key: "status",     width: 18 },
      ],
      rows: filtered.map(v => ({
        ...v,
        sev_label: (v.severity || "").toUpperCase(),
        crew_name: v.crew_member?.name || v.crew_name || "",
      })),
      filename: `compliance-violations-${todayStr()}.pdf`,
      orientation: "landscape",
    });
  }

  return (
    <div className="p-6 space-y-5 fade-in-up" data-testid="compliance-board">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-1">Crew Rostering · Module C</div>
          <h1 className="text-2xl font-black font-heading text-foreground">Compliance Engine</h1>
          <p className="text-xs text-muted-foreground mt-1">DGCA rules evaluation — violations + cumulative FDP limits + WOCL analysis</p>
        </div>
        <button onClick={runCheck} disabled={checking}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-sm text-sm font-semibold hover:bg-primary/90 disabled:opacity-50"
          data-testid="run-compliance-check">
          <RefreshCw size={14} className={checking ? "animate-spin" : ""} />
          {checking ? "Checking..." : "Run Full Check"}
        </button>
      </div>

      {/* ── KPI badges ─────────────────────────────────────────────────────── */}
      {activeTab === "violations" && (
        <div className="grid grid-cols-3 gap-3">
          {[["Errors", errorCount, "text-red-400", "border-red-500/20"], ["Warnings", warningCount, "text-amber-400", "border-amber-500/20"], ["Rules Active", rules.length, "text-green-400", "border-border"]].map(([lbl, val, col, border]) => (
            <div key={lbl} className={`bg-card border ${border} rounded-sm p-4`}>
              <div className={`text-3xl font-mono font-medium ${col}`}>{val}</div>
              <div className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">{lbl}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Tab switcher ────────────────────────────────────────────────────── */}
      <div className="flex gap-1 bg-background/60 border border-border rounded-sm p-1 w-fit">
        {[
          { id: "violations", label: "Violations", icon: AlertTriangle },
          { id: "dgca",       label: "DGCA Limits", icon: Shield },
        ].map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setActiveTab(id)}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-sm text-xs font-bold transition-colors ${activeTab === id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
            data-testid={`compliance-tab-${id}`}>
            <Icon size={11} /> {label}
          </button>
        ))}
      </div>

      {/* ── Violations tab ──────────────────────────────────────────────────── */}
      {activeTab === "violations" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex gap-2">
              {["", "error", "warning", "info"].map(sev => (
                <button key={sev} onClick={() => setFilterSeverity(sev)}
                  className={`px-3 py-1 rounded-sm text-xs font-semibold border transition-colors ${filterSeverity === sev ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:text-foreground"}`}>
                  {sev || "All"}
                </button>
              ))}
            </div>
            <button onClick={downloadViolationsPDF}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 border border-primary/30 text-primary rounded-sm text-xs font-bold hover:bg-primary/20 transition-colors"
              data-testid="compliance-download-pdf">
              <Download size={12} /> Export PDF
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <RefreshCw size={16} className="animate-spin mr-2" /> Loading...
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <CheckCircle size={28} className="text-green-400 mb-2" />
              <div className="text-sm font-medium">No violations found</div>
              <div className="text-xs mt-1">Run the compliance check to evaluate the current roster</div>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-sm overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border bg-background/60">
                    {["Severity", "Rule", "Crew", "Description", "Date", "Status"].map(h => (
                      <th key={h} className="text-left px-4 py-2.5 text-muted-foreground font-bold uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((v, i) => (
                    <tr key={v.id || i} className={`border-b border-border/40 hover:bg-secondary/20 ${i % 2 === 0 ? "" : "bg-background/20"}`}>
                      <td className="px-4 py-2.5">
                        <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-sm ${v.severity === "error" ? "text-red-400 bg-red-500/10 border border-red-500/30" : v.severity === "warning" ? "text-amber-400 bg-amber-500/10 border border-amber-500/30" : "text-blue-400 bg-blue-500/10 border border-blue-500/30"}`}>
                          {(v.severity || "info").toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-blue-400">{v.rule_code || v.rule}</td>
                      <td className="px-4 py-2.5 font-medium text-foreground">{v.crew_member?.name || v.crew_name || "—"}</td>
                      <td className="px-4 py-2.5 text-muted-foreground max-w-xs truncate">{v.description || v.message}</td>
                      <td className="px-4 py-2.5 text-muted-foreground font-mono">{v.date || v.violation_date || "—"}</td>
                      <td className="px-4 py-2.5">
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${v.status === "resolved" ? "text-green-400 bg-green-500/10" : "text-amber-400 bg-amber-500/10"}`}>
                          {(v.status || "open").toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── DGCA Limits tab ─────────────────────────────────────────────────── */}
      {activeTab === "dgca" && <DGCATab />}
    </div>
  );
}
